import {
  Footprints,
  Home,
  HeartHandshake,
  Sun,
  DoorOpen,
} from "lucide-react";

export const IMAGES = {
  logoHorizontal: "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/71vos77i_logo-horiz.webp",
  logoHorizontalWhite: "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/bjta1u23_logo-horiz-white.webp",
  logoVertical: "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/afali4pk_logo-vertical.webp",
  hero: "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/ldxfvtzq_main-banner.jpg",
  walking:
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/x8wiw67i_dog-walking.jpg",
  hosting:
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/8ebbckcj_dog-hosting.jpg",
  sitting:
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/22xjx735_dog-sitting.jpg",
  daycare:
    "https://images.unsplash.com/photo-1633587481886-be96064e89ec?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&auto=format&w=700&q=70",
  houseVisits:
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/vrbxi4gk_house-visits.jpg",
  about:
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/9d0nxla6_duo.jpg",
};

export const SERVICES = [
  {
    id: "hosting",
    name: "Dog Boarding",
    price: "From $69/night",
    tag: "Overnight at our home",
    icon: Home,
    image: IMAGES.hosting,
    desc: "A cosy home away from home exclusively for your pup. No other animals! Just couch cuddles, plenty of playtime and round-the-clock care from a caring couple.",
  },
  {
    id: "walking",
    name: "Dog Walking",
    price: "From $39/walk",
    tag: "Morning & daytime",
    icon: Footprints,
    image: IMAGES.walking,
    desc: "Fun, tail-wagging adventures around the streets and parks of South Brisbane! Pick up and drop off included, plus we'll share plenty of photo updates and track it with GPS.",
  },
  {
    id: "daycare",
    name: "Daycare",
    price: "From $49/day",
    tag: "During work hours",
    icon: Sun,
    image: IMAGES.daycare,
    desc: "Give your furry friend some loving company and plenty of playtime while you're at work. Simply drop them off in the morning and collect your happy (and very tired!) pup after work.",
  },
  {
    id: "sitting",
    name: "Dog Sitting",
    price: "From $79/night",
    tag: "Overnight at your home",
    icon: HeartHandshake,
    image: IMAGES.sitting,
    desc: "We stay at your place so your dog keeps their routine, bed and home comforts while you're away. Minimal stress, maximum love!",
  },
  {
    id: "house-visits",
    name: "House Visits",
    price: "From $29/check-in",
    tag: "Welfare check",
    icon: DoorOpen,
    image: IMAGES.houseVisits,
    desc: "We'll pop by to check on your best friend, make sure they have some food, fresh water, a toilet break and a good pat!",
  },
];

// Options shown in the booking "Choose your service" popup.
// `overnight: true` => uses From/End dates. Otherwise => how-often + add days.
export const SERVICE_OPTIONS = [
  { id: "hosting", name: "Pet hosting", desc: "Your pet will stay over at our home", icon: Home, overnight: true },
  { id: "walks", name: "Dog walks", desc: "Fun and exercise for your dog", icon: Footprints, overnight: false },
  { id: "daycare", name: "Pet daycare", desc: "Pet care during working hours", icon: Sun, overnight: false },
  { id: "sitting", name: "Pet sitting", desc: "We'll stay overnight at your home", icon: HeartHandshake, overnight: true },
  { id: "housevisits", name: "House visits", desc: "A drop-in to check on your pet", icon: DoorOpen, overnight: false },
];

// Maps a service card id (SERVICES) to a booking SERVICE_OPTIONS id.
export const CARD_TO_SERVICE = {
  hosting: "hosting",
  sitting: "sitting",
  daycare: "daycare",
  "house-visits": "housevisits",
  walking: "walks",
};


export const MANIFESTO = [
  {
    n: "01",
    title: "Local & experienced doggo lovers",
    body: "Our walks are GPS-tracked on Strava and you receive photo & video updates so you always know exactly how your furry friend is doing!",
  },
  {
    n: "02",
    title: "Exclusive one-household care",
    body: "We never mix dogs from different families without your say so. Whether we're walking or hosting, your dog only shares our attention with other dogs from the same home.",
  },
  {
    n: "03",
    title: "Convenient home pick-up & drop-off",
    body: "We collect and return your dog at your place while you're out or at work, making quality care fit seamlessly around your schedule.",
  },
  {
    n: "04",
    title: "Better rates for regular clients",
    body: "We love to reward our loyal customers, so if you commit to ongoing services you'll receive better rates!",
  },
];

export const FAQS = [
  {
    q: "Which areas do you cover?",
    a: "We keep our operation in our local area of South Brisbane and the immediate surrounding suburbs, such as West End, South Bank, Highgate Hill, Milton, Petrie Terrace, Paddington & the CBD. We may be willing to come out further but a fuel surcharge may be required on the booking.<br/><br/>Pop your suburb into the <a href=\"#book\">booking form</a> and we'll confirm availability.",
  },
  {
    q: "Do you only look after dogs?",
    a: "Yes, strictly dogs only. We want to be fully focused on giving your pup the best possible experience.",
  },
  {
    q: "What's the difference between hosting and sitting?",
    a: "Hosting means your dog stays cosy at our home as part of the family. Sitting means we come to your place so your dog keeps their own bed and routine.",
  },
  {
    q: "How much is dog walking, hosting & daycare?",
    a: "We display our prices in the <a href=\"#services\">Services section</a> on this page.<br/><br/>If you become a regular client, we can also offer you cheaper rates.<br/><br/>Our prices aren't the cheapest, but we don't like to cut corners, and these rates allow us to keep every experience safe, fun and reliable for your furry friend.",
  },
  {
    q: "What is the booking process for dog care services?",
    a: "Just <a href=\"#book\">request a booking</a> on this page and tell us as much as you can about your dog(s) and the service you need. We'll get back to you (usually within an hour) with pricing and availability.<br/><br/>If you're happy to go ahead, we'll arrange a free meet & greet so we can meet you and your pup(s) and ensure we're the right fit. If the stars align, we'll then request payment of the service prior to the commencement date.",
  },
  {
    q: "Will my dog(s) be safe in your care?",
    a: "Absolutely! We will get to know your dogs needs, follow your feeding and medication instructions to the letter, and send you updates throughout our time with them.<br/><br/>We have over 20+ years of combined experience between us, so you can rest assured your furry friend will be safe with us.<br/><br/>We'll also ensure we have an emergency contact on file just in case.",
  },
];

export const CONTACT = {
  phone: "+61494593518",
  phoneDisplay: "0494 593 518",
  sms: "+61494593518",
  email: "cheekypupsbrisbane@gmail.com",
  area: "South Brisbane, QLD",
};

export const REVIEWS = [
  {
    name: "Luciana S.",
    service: "Hosting",
    date: "Aug 2026",
    stars: 5,
    quote: "Lee took great care of our dog Luna. Thank you, Lee!",
    photo:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/tmk6foo6_Luciana.jpg",
    photoPosition: "30% 35%",
  },
  {
    name: "Yelina B.",
    service: "Hosting",
    date: "Aug 2026",
    stars: 5,
    quote: "Amazing with my puppy! Would definitely recommend.",
    photo:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/a9udrfe2_Yelina.jpg",
    photoPosition: "55% 45%",
  },
  {
    name: "Meegin M.",
    service: "Day care",
    date: "Jul 2026",
    stars: 5,
    quote:
      "I had two very happy dogs to pick up at the end of each day. Thank you again!",
    photo:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_pups-care/artifacts/4ckfp4va_Meegin.jpg",
    photoPosition: "center 25%",
  },
];
